import {
    DealerDetails,
    DealerProfileInfo,
} from '../../../models/osb-model/osb-dealer-info';
import OsbUtilService from '../osb-util-service/osb-util-service';
import GoogleMapService from './google-map-service';

export default class DealerSearchService {
    private static readonly dealerGeocodeCache = new Map<
        string,
        { lat: number; lng: number }
    >();

    private static readonly geocodeAddressWithoutCountryFilter = (
        address: string
    ): Promise<{ lat: number; lng: number } | null> => {
        return new Promise(resolve => {
            if (typeof window === 'undefined' || !window?.google?.maps) {
                resolve(null);
                return;
            }

            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ address }, (results, status) => {
                if (
                    status === window.google.maps.GeocoderStatus.OK &&
                    results?.[0]?.geometry?.location
                ) {
                    const lat = Number(results[0].geometry.location.lat());
                    const lng = Number(results[0].geometry.location.lng());
                    resolve(
                        Number.isFinite(lat) && Number.isFinite(lng)
                            ? { lat, lng }
                            : null
                    );
                    return;
                }

                resolve(null);
            });
        });
    };

    private static readonly resolveDealerCoordinates = (
        geoCountryCode: string,
        dealer: DealerProfileInfo
    ): Promise<{ lat: number; lng: number }> => {
        const address = [dealer?.street, dealer?.town, dealer?.postalCode]
            .filter(Boolean)
            .join(', ')
            .trim();
        const cacheKey = `${String(
            geoCountryCode || ''
        ).toLowerCase()}|${address.toLowerCase()}`;

        if (!address) {
            return Promise.resolve({ lat: 0, lng: 0 });
        }

        const cachedCoordinates = DealerSearchService.dealerGeocodeCache.get(
            cacheKey
        );
        if (cachedCoordinates) {
            return Promise.resolve(cachedCoordinates);
        }

        return new Promise(resolve => {
            GoogleMapService.getGeocodeLocation(
                geoCountryCode,
                address,
                (geocode: any[]) => {
                    const lat = Number(geocode?.[0]?.lat);
                    const lng = Number(geocode?.[0]?.lng);
                    if (Number.isFinite(lat) && Number.isFinite(lng)) {
                        const coordinates = { lat, lng };
                        DealerSearchService.dealerGeocodeCache.set(
                            cacheKey,
                            coordinates
                        );
                        resolve(coordinates);
                        return;
                    }

                    void DealerSearchService.geocodeAddressWithoutCountryFilter(
                        address
                    ).then(fallbackCoordinates => {
                        if (fallbackCoordinates) {
                            DealerSearchService.dealerGeocodeCache.set(
                                cacheKey,
                                fallbackCoordinates
                            );
                            resolve(fallbackCoordinates);
                            return;
                        }

                        resolve({ lat: 0, lng: 0 });
                    });
                }
            );
        });
    };

    static readonly bingDealerSearchByLocation = (
        conf: any,
        radius: number,
        maxResult: number,
        callback: any,
        polygon: string
    ) => {
        if (conf) {
            if (conf.length > 0) {
                const matchParameters = { CountryCode: '' };
                matchParameters.CountryCode = OsbUtilService.getAppConfiguration().threeLetterCountryCode;
                GoogleMapService.searchDealersByDistance(
                    conf[0],
                    radius,
                    maxResult,
                    (dealers: any) => {
                        callback(dealers);
                    },
                    matchParameters,
                    polygon
                );
            }
        }
    };

    static readonly bingDealerByName = (
        maxResult: number,
        callback: any,
        polygon: string,
        dealerCode: string,
        osbTwoLetterCountryCode: string,
        marketDealers: DealerProfileInfo[] = [],
        geoCountryCode = ''
    ) => {
        void polygon;
        void marketDealers;
        void geoCountryCode;

        const normalizedDealerCode = String(dealerCode || '').trim();
        const normalizedCountryCode = String(
            osbTwoLetterCountryCode || ''
        ).trim();

        if (!normalizedDealerCode) {
            callback([]);
            return;
        }

        const candidateDealerId = normalizedCountryCode
            ? `${normalizedCountryCode}${normalizedDealerCode}`
            : normalizedDealerCode;

        const maxResultsToReturn = Number(maxResult) || 0;

        GoogleMapService.searchDealerById(
            candidateDealerId,
            (dealers: any[]) => {
                const validDealers = Array.isArray(dealers)
                    ? dealers.slice(0, maxResultsToReturn)
                    : [];
                callback(validDealers);
            }
        );
    };

    static readonly getIdFromBing = (
        item: { DealerID: string },
        numberOfLeadingCharToStrip: number
    ) => {
        return (
            item.DealerID &&
            (numberOfLeadingCharToStrip > 0
                ? item.DealerID.substring(numberOfLeadingCharToStrip).trim()
                : item.DealerID.trim())
        );
    };

    static readonly filterOnOsbDealerIds = (
        allOsbDealers: DealerDetails[] | any, //NOSONAR
        bingDealers: any,
        numberOfLeadingCharToStrip: number
    ) => {
        if (!allOsbDealers || !Array.isArray(allOsbDealers)) {
            return [];
        }
        let _osbEnabledDealerFromBing: any = [];
        allOsbDealers.forEach((dealer: { dealerCode: any }) => {
            const dealerCode = dealer.dealerCode.trim();
            _osbEnabledDealerFromBing.push(
                bingDealers.find(
                    (d: { DealerID: string }) =>
                        DealerSearchService.getIdFromBing(
                            d,
                            numberOfLeadingCharToStrip
                        ) === dealerCode
                )
            );
        });
        // remove undefined objects from the array.
        _osbEnabledDealerFromBing = _osbEnabledDealerFromBing.filter(function(
            element: undefined
        ) {
            return element !== undefined;
        });
        return _osbEnabledDealerFromBing;
    };
}
